/**
 * Somente FORMATAÇÃO e normalização de texto digitado. Nenhuma aritmética monetária acontece na tela:
 * saldos, totais e conversões vêm prontos do backend (fonte única de verdade).
 */
export type Currency = "BRL" | "USD";
const SYMBOL: Record<Currency, string> = { BRL: "R$", USD: "US$" };

/** "-1234.50" -> "-R$ 1.234,50" (manipulação de texto; sem Number/float). */
export function formatMoney(decimal: string, currency: Currency): string {
  const neg = decimal.startsWith("-");
  const [int = "0", frac = "00"] = (neg ? decimal.slice(1) : decimal).split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${neg ? "-" : ""}${SYMBOL[currency]} ${grouped},${frac.padEnd(2, "0")}`;
}

/** Entrada do usuário ("1.234,56", "1234,5", "-10") -> texto decimal canônico ("1234.56"). Valida o formato; o backend revalida. */
export function normalizeMoneyInput(raw: string): string | null {
  let t = raw.trim().replace(/\s/g, "");
  if (!t) return null;
  if (t.includes(",")) t = t.replace(/\./g, "").replace(",", ".");
  else if ((t.match(/\./g) ?? []).length > 1) t = t.replace(/\./g, "");
  return /^-?\d{1,14}(\.\d{1,2})?$/.test(t) ? t : null;
}

export const fmtDay = (iso: string) => { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; };

/** Data de hoje (AAAA-MM-DD) no fuso de São Paulo, para calcular dias em atraso na tela. */
export const todayISO = () => new Intl.DateTimeFormat("sv-SE", { timeZone: "America/Sao_Paulo" }).format(new Date());

/** Limita o texto digitado a N casas decimais (aceita "1.234,56" e "1234.5"; o sinal "-" só no início). Só texto, sem aritmética. */
export function limitDecimals(raw: string, n = 2): string {
  let t = raw.replace(/[^\d.,-]/g, "");
  const neg = t.startsWith("-");
  t = t.replace(/-/g, "");
  const ci = t.indexOf(",");
  let out: string;
  if (ci >= 0) {
    const tail = t.slice(ci + 1).replace(/[.,]/g, "").slice(0, n);
    out = n > 0 ? `${t.slice(0, ci)},${tail}` : t.slice(0, ci);
  } else if ((t.match(/\./g) ?? []).length === 1) {
    const i = t.indexOf(".");
    out = n > 0 ? `${t.slice(0, i)}.${t.slice(i + 1).slice(0, n)}` : t.slice(0, i);
  } else out = t;
  return (neg ? "-" : "") + out;
}

/**
 * Máscara de dinheiro "estilo caixa eletrônico": os dígitos entram pela direita (1 -> 0,01; 10 -> 0,10; 1050 -> 10,50).
 * Só texto, sem aritmética. Vazio continua vazio (mostra o placeholder). `negative` permite o sinal "-".
 */
export function maskMoneyTyping(raw: string, negative = false): string {
  const neg = negative && raw.includes("-");
  const all = raw.replace(/\D/g, "");
  if (!all) return neg ? "-" : "";            // nada digitado: continua vazio (mostra o placeholder)
  const digits = all.replace(/^0+/, "").slice(0, 16);
  const d = digits.padStart(3, "0");           // só zeros digitados => "0,00" (zero é um valor válido)
  const int = d.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${neg ? "-" : ""}${int},${d.slice(-2)}`;
}

/** Valor vindo do estado/servidor ("50", "50.5", "50,5", "1234.50") -> texto mascarado ("50,00", "50,50", "1.234,50"). */
export function displayMoneyValue(v: string, negative = false): string {
  if (!v || v === "-") return v;
  const neg = v.startsWith("-");
  const t = v.replace("-", "");
  let int = t, frac = "";
  if (t.includes(",")) { const i = t.lastIndexOf(","); int = t.slice(0, i); frac = t.slice(i + 1); }
  else if ((t.match(/\./g) ?? []).length === 1) { const i = t.indexOf("."); int = t.slice(0, i); frac = t.slice(i + 1); }
  const digits = int.replace(/\D/g, "") + frac.replace(/\D/g, "").padEnd(2, "0").slice(0, 2);
  return maskMoneyTyping((neg && negative ? "-" : "") + digits, negative);
}
